import * as React from "react";
import Paper from "@mui/material/Paper";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import Grid from "@mui/material/Grid2";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsValidPayloadDetails,
  setMapDetailsDataPayload,
} from "../../../../../features/mapsSlice";
import { DESTINATION_TABLE_COLUMN_FIELD } from "../helpers/destinationTableColumn";

interface Props {
  tableHeadings: { [key: string]: string };
  rows: any[];
  selectedInterface: any;
  selectedDestinationTable: any;
  isFetchingReports: boolean;
}

const DuplicateMapDetails = ({
  tableHeadings,
  selectedInterface,
  selectedDestinationTable,
  isFetchingReports,
}: Props) => {
  const [openCalculationAlert, setOpenCalculationAlert] = React.useState(false);
  const [openDefinitionAlert, setOpenDefinitionAlert] = React.useState(false);
  const [selectedRows, setSelectedRows] = React.useState<Set<string>>(
    new Set(),
  );
  const [selectAll, setSelectAll] = React.useState(true);
  const apiRef = React.useRef<any>(null);
  const {
    selectedDestinationTableIDMaps,
    uniqueDefinitionsMapsData,
    calculationTypesMapsData,
    mapDetails,
    interfaceReportsData,
  } = useSelector((state: any) => state.maps);

  React.useEffect(() => {
    setGridRows((prevRows) =>
      prevRows.map((row) => ({
        ...row,
        report_id: "",
        unique_definition: "",
      })),
    );
  }, [selectedInterface]);

  const filteredUniqueDefinitions = React.useMemo(() => {
    if (
      !uniqueDefinitionsMapsData ||
      !selectedInterface ||
      !selectedDestinationTable
    ) {
      return [];
    }

    return uniqueDefinitionsMapsData.filter(
      (u: any) =>
        u.affiliate_system_interface === selectedInterface.label &&
        u.destination_table_name === selectedDestinationTable,
    );
  }, [uniqueDefinitionsMapsData, selectedInterface, selectedDestinationTable]);

  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setSelectedRows(new Set());
      setSelectAll(false);
    } else {
      // Select all
      const allRowIds: any = new Set(destinationRows.map((row) => row.id));
      setSelectedRows(allRowIds);
      setSelectAll(true);
    }
  };
  const dispatch = useDispatch();
  const destinationTableColumns = React.useMemo(() => {
    return (
      DESTINATION_TABLE_COLUMN_FIELD[
        selectedDestinationTableIDMaps as keyof typeof DESTINATION_TABLE_COLUMN_FIELD
      ] || []
    );
  }, [selectedDestinationTableIDMaps]);
  const validatePayload = React.useCallback((payload: any[]) => {
    if (!payload || payload.length === 0) return false;

    return payload.every((row) =>
      Object.values(row).every(
        (value) => value !== "" && value !== null && value !== undefined,
      ),
    );
  }, []);
  const handleCellClick = (params: any) => {
    // Only enable single-click edit for source_field_expression column
    // and only if the row is selected
    if (
      params.field === "source_field_expression" &&
      selectedRows.has(params.row.id)
    ) {
      if (apiRef.current) {
        apiRef.current.startCellEditMode({
          id: params.id,
          field: params.field,
        });
      }
    }
  };

  const destinationRows = React.useMemo(() => {
    if (!selectedDestinationTableIDMaps) {
      return [];
    }
    // Create a map of existing field mappings by destination field name
    const fieldMappingMap = new Map();
    if (mapDetails?.field_mapping) {
      mapDetails.field_mapping.forEach((mapping: any) => {
        fieldMappingMap.set(mapping.destination_field_name, mapping);
      });
    }

    return destinationTableColumns.map((column) => {
      const existingMapping = fieldMappingMap.get(column.title);

      return {
        id: column.id,
        destination_table_column: column.title,
        source_field_expression: existingMapping?.source_field_expression || "", // Prefilled or empty
        calculation_type: existingMapping?.uni_field_calculation_type_id || "", // Prefilled or empty
        unique_definition: existingMapping?.raw_data_unique_definition_id || "", // Prefilled or empty
        report_id: existingMapping?.report_id || "",
      };
    });
  }, [
    destinationTableColumns,
    mapDetails?.field_mapping,
    selectedDestinationTableIDMaps,
  ]);
  const [gridRows, setGridRows] = React.useState(destinationRows);
  const updateMapDetailsDataPayload = React.useCallback(() => {
    const payload = gridRows
      .filter((row: any) => selectedRows.has(row.id)) // Only include selected
      .map((row) => ({
        source_field_expression: row.source_field_expression,
        destination_field_name: row.destination_table_column,
        uni_field_calculation_type_id: row.calculation_type,
        raw_data_unique_definition_id: row.unique_definition,
        report_id: row.report_id,
      }));

    // Dispatch action to update mapDetailsDataPayload in Redux
    dispatch(setMapDetailsDataPayload(payload));
    dispatch(setIsValidPayloadDetails(validatePayload(payload)));
  }, [gridRows, selectedRows, dispatch, validatePayload]);

  React.useEffect(() => {
    updateMapDetailsDataPayload();
  }, [updateMapDetailsDataPayload]);
  React.useEffect(() => {
    if (!selectedDestinationTableIDMaps || destinationRows.length === 0) {
      setSelectedRows(new Set());
      setGridRows([]);
      return;
    }

    // Pre-select rows that have existing field mappings
    const preSelectedRows = new Set<string>();
    if (mapDetails?.field_mapping) {
      destinationRows.forEach((row: any) => {
        const hasExistingMapping = mapDetails.field_mapping.some(
          (mapping: any) =>
            mapping.destination_field_name === row.destination_table_column,
        );
        if (hasExistingMapping) {
          preSelectedRows.add(row.id);
        }
      });
    }
    if (preSelectedRows.size === 0) {
      const allRowIds: any = new Set(destinationRows.map((row) => row.id));
      setSelectedRows(allRowIds);
    } else {
      setSelectedRows(preSelectedRows);
    }

    setGridRows(destinationRows);
    setSelectAll(true);
  }, [
    destinationRows,
    mapDetails?.field_mapping,
    selectedDestinationTableIDMaps,
  ]);

  const handleCheckboxChange = (rowId: string, checked: boolean) => {
    setSelectedRows((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(rowId);
      } else {
        newSelected.delete(rowId);
      }
      setSelectAll(newSelected.size === destinationRows.length);
      return newSelected;
    });
  };

  const handleCalculationTypeChange = (rowId: string, value: string) => {
    setGridRows((prevRows) =>
      prevRows.map((row: any) =>
        row.id === rowId ? { ...row, calculation_type: value } : row,
      ),
    );
  };

  const handleUniqueDefinitionChange = (rowId: string, value: string) => {
    setGridRows((prevRows) =>
      prevRows.map((row: any) =>
        row.id === rowId ? { ...row, unique_definition: value } : row,
      ),
    );
  };

  const handleReportChange = (rowId: string, value: string) => {
    setGridRows((prevRows) =>
      prevRows.map((row: any) =>
        row.id === rowId ? { ...row, report_id: value } : row,
      ),
    );
  };
  const handleNavigateToCalculationType = () => {
    setOpenCalculationAlert(true);
  };

  const handleNavigateToUniqueDefinition = () => {
    setOpenDefinitionAlert(true);
  };

  const handleClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenCalculationAlert(false);
    setOpenDefinitionAlert(false);
  };

  const handleConfirm = () => {
    window.open("/data_collection/calculation_types", "_blank");
    setOpenCalculationAlert(false);
  };

  const handleConfirmDefinition = () => {
    window.open(
      "/data_collection/unique_definitions/create_new_definition",
      "_blank"
    );
    setOpenDefinitionAlert(false);
  };

  const processRowUpdate = (newRow: any) => {
    const normalizedRow = {
      ...newRow,
      source_field_expression:
        typeof newRow.source_field_expression === "string"
          ? newRow.source_field_expression.trim()
          : newRow.source_field_expression,
    };

    setGridRows((prevRows) =>
      prevRows.map((row) =>
        row.id === normalizedRow.id ? normalizedRow : row,
      ),
    );
    return normalizedRow;
  };

  const handleProcessRowUpdateError = (error: Error) => {
    console.error("Error updating row:", error);
  };

  React.useEffect(() => {
    setGridRows(destinationRows);
  }, [destinationRows]);

  // Create columns based on tableHeadings
  const columns: GridColDef[] = Object.entries(tableHeadings).map(
    ([key, value], index) => {
      const baseColumn: GridColDef = {
        field: key,
        headerName: value,
        flex: 1,
      };

      // Custom header renderer for calculation_type and unique_definition columns
      if (index === 2) {
        // calculation_type column
        baseColumn.renderHeader = () => (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="subtitle2">{value}</Typography>
            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={handleNavigateToCalculationType}
            >
              Add new type
              <AddIcon sx={{ width: "20px", ml: "5px" }} />
            </Button>
          </Box>
        );
      }

      if (index === 3) {
        // unique_definition column
        baseColumn.renderHeader = () => (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="subtitle2">{value}</Typography>
            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={handleNavigateToUniqueDefinition}
            >
              Add new definition
              <AddIcon sx={{ width: "20px", ml: "5px" }} />
            </Button>
          </Box>
        );
      }

      // Custom cell renderers based on column type
      if (key === "destination_table_column") {
        baseColumn.renderCell = (params: GridRenderCellParams) => {
          const isSelected = selectedRows.has(params.row.id);
          return (
            <Box display="flex" alignItems="center">
              <Checkbox
                color="secondary"
                sx={{ marginRight: 1 }}
                checked={isSelected}
                onChange={(e) =>
                  handleCheckboxChange(params.row.id, e.target.checked)
                }
              />
              <Typography
                sx={{
                  color: isSelected ? "inherit" : "rgba(0, 0, 0, 0.38)",
                }}
              >
                {params.value || "N/A"}
              </Typography>
            </Box>
          );
        };
      } else if (key === "source_field_expression") {
        // Make this column editable
        baseColumn.editable = true;
        baseColumn.renderCell = (params: GridRenderCellParams) => {
          const isSelected = selectedRows.has(params.row.id);
          return (
            <Typography
              sx={{
                color: isSelected ? "inherit" : "rgba(0, 0, 0, 0.38)",
                paddingTop: "12px",
              }}
            >
              {params.value || "Please enter source expression"}
            </Typography>
          );
        };
      } else if (key === "calculation_type") {
        baseColumn.sortable = false;
        baseColumn.renderCell = (params: GridRenderCellParams) => {
          const isSelected = selectedRows.has(params.row.id);
          if (!isSelected) {
            // Show name instead of id when row is disabled
            const found = calculationTypesMapsData?.find(
              (c: any) => c.id === params.value,
            );
            return (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                sx={{ color: "rgba(0, 0, 0, 0.38)" }}
              >
                <Typography
                  sx={{ color: "rgba(0, 0, 0, 0.38)", paddingTop: "12px" }}
                >
                  {found?.name || "Choose from list"}
                </Typography>
                <ArrowDropDownIcon sx={{ color: "rgba(0, 0, 0, 0.38)" }} />
              </Box>
            );
          }

          return (
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                value={params.value || ""}
                onChange={(e) =>
                  handleCalculationTypeChange(params.row.id, e.target.value)
                }
                displayEmpty
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSelect-select": {
                    padding: "8px 12px",
                    minHeight: "unset",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 224,
                    },
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography color="textSecondary">
                        Choose from list
                      </Typography>
                    );
                  }
                  const found = calculationTypesMapsData?.find(
                    (c: any) => c.id === selected,
                  );
                  return found ? found.name : selected;
                }}
              >
                {calculationTypesMapsData?.map((calculationType: any) => (
                  <MenuItem key={calculationType.id} value={calculationType.id}>
                    {calculationType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        };
      } else if (key === "unique_definition") {
        baseColumn.sortable = false;
        baseColumn.renderCell = (params: GridRenderCellParams) => {
          const isSelected = selectedRows.has(params.row.id);
          if (!isSelected) {
            return (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                sx={{
                  color: "rgba(0, 0, 0, 0.38)",
                }}
              >
                <Typography
                  sx={{
                    color: "rgba(0, 0, 0, 0.38)",
                    paddingTop: "12px",
                  }}
                >
                  {params.value || "Choose from list"}
                </Typography>
                <ArrowDropDownIcon
                  sx={{
                    color: "rgba(0, 0, 0, 0.38)",
                  }}
                />
              </Box>
            );
          }

          return (
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                value={params.value || ""}
                onChange={(e) =>
                  handleUniqueDefinitionChange(params.row.id, e.target.value)
                }
                displayEmpty
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSelect-select": {
                    padding: "8px 12px",
                    minHeight: "unset",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 224,
                    },
                  },
                }}
                renderValue={(selected) => {
                  const found = filteredUniqueDefinitions?.find(
                    (u: any) => u.id === selected,
                  );
                  if (!selected || !found) {
                    return (
                      <Typography color="textSecondary">
                        Choose from list
                      </Typography>
                    );
                  }

                  return found && `ID ${found.id}`;
                }}
              >
                {filteredUniqueDefinitions?.length ? (
                  filteredUniqueDefinitions?.map((uniqueDefinition: any) => (
                    <MenuItem
                      key={uniqueDefinition.id}
                      value={uniqueDefinition.id}
                    >
                      {`ID ${uniqueDefinition.id}`}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    No options
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          );
        };
      } else if (key === "report_id") {
        baseColumn.sortable = true;
        baseColumn.renderCell = (params: GridRenderCellParams) => {
          const isSelected = selectedRows.has(params.row.id);

          if (!isSelected) {
            const found = interfaceReportsData?.find(
              (r: any) => r.id === params.value,
            );
            return (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                sx={{ color: "rgba(0, 0, 0, 0.38)" }}
              >
                <Typography sx={{ paddingTop: "12px" }}>
                  {found?.report_name || "Choose from list"}
                </Typography>
                <ArrowDropDownIcon sx={{ color: "rgba(0, 0, 0, 0.38)" }} />
              </Box>
            );
          }

          return (
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                value={params.value || ""}
                onChange={(e) =>
                  handleReportChange(params.row.id, e.target.value)
                }
                displayEmpty
                disabled={isFetchingReports}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& .MuiSelect-select": {
                    padding: "8px 12px",
                    minHeight: "unset",
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography color="textSecondary">
                        Choose from list
                      </Typography>
                    );
                  }
                  const found = interfaceReportsData?.find(
                    (r: any) => r.id === selected,
                  );
                  return found?.report_name || selected;
                }}
              >
                {interfaceReportsData?.length ? (
                  interfaceReportsData.map((report: any) => (
                    <MenuItem key={report.id} value={report.id}>
                      {report.report_name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled value="">
                    No options
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          );
        };
      } else {
        baseColumn.renderCell = (params: GridRenderCellParams) => {
          const isSelected = selectedRows.has(params.row.id);
          return (
            <Typography
              sx={{
                color: isSelected ? "inherit" : "rgba(0, 0, 0, 0.38)",
                paddingTop: "12px",
              }}
            >
              {params.value || "Choose from list"}
            </Typography>
          );
        };
      }
      return baseColumn;
    },
  );

  // Prepare rows with id field (required by DataGrid)
  const dataGridRows = gridRows;
  return (
    <>
      <Alert
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        severity="warning"
        sx={{ alignItems: "center" }}
      >
        <Typography
          variant="body1"
          sx={{ color: "#ED6C02", fontWeight: "500" }}
        >
          Ensure the input is valid! Incorrect values can trigger system errors.
        </Typography>
      </Alert>

      <Paper sx={{ mt: 2 }}>
        <Box sx={{ padding: "8px 16px" }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Grid size={8}>
              <Typography variant="h3" sx={{ fontSize: "24px" }}>
                Map Details
              </Typography>
            </Grid>
            <Grid
              size={4}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Checkbox
                color="secondary"
                checked={selectAll}
                indeterminate={
                  selectedRows.size > 0 &&
                  selectedRows.size < destinationRows.length
                }
                onChange={handleSelectAll}
              />
              <Typography variant="body1">Select All</Typography>
            </Grid>
          </Grid>
        </Box>

        <DataGrid
          rows={dataGridRows}
          columns={columns}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          onCellClick={handleCellClick}
          apiRef={apiRef}
          hideFooterPagination
          disableColumnMenu
          hideFooterSelectedRowCount
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            !selectedRows.has(params.row.id) ? "disabled-row" : ""
          }
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                }}
              >
                <Typography variant="body1" sx={{ mt: 1 }}>
                  No data available
                </Typography>
              </Box>
            ),
          }}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
              outline: "none", // 👈 remove focus outline
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none", // 👈 remove focus border when clicked
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none !important", // 👈 remove cell outline
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
              {
                outline: "none !important", // 👈 remove header outline
              },
            "& .MuiDataGrid-columnHeaders": {
              borderBottom: "2px solid rgba(224, 224, 224, 1)",
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "transparent !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "transparent !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
              width: "100%",
            },
          }}
        />
      </Paper>

      <Snackbar
        open={openCalculationAlert}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity="warning"
          action={
            <>
              <Button color="inherit" size="small" onClick={handleClose}>
                NO
              </Button>
              <Button color="inherit" size="small" onClick={handleConfirm}>
                YES
              </Button>
            </>
          }
        >
          You will be redirecting into the Calculation Type Module
        </Alert>
      </Snackbar>

      <Snackbar
        open={openDefinitionAlert}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity="warning"
          action={
            <>
              <Button color="inherit" size="small" onClick={handleClose}>
                NO
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={handleConfirmDefinition}
              >
                YES
              </Button>
            </>
          }
        >
          You will be redirecting into the Unique Definition Module
        </Alert>
      </Snackbar>
    </>
  );
};

export default DuplicateMapDetails;
